/* eslint-disable max-len */

/**
 * Ensures a function only gets executed at most once every `limit` ms.
 * Useful in situations where you want to limit the rate at which a
 * function is called, for example, when listening to high-frequency
 * events like scrolling or resizing in the browser.
 *
 * @param func  - The function to throttle.
 * @param limit - The minimum time in milliseconds between calls to `func`.
 *
 * @return A new function that when called, will call `func` at most once
 * 	every `limit` milliseconds.
 */
type ThrottleFunctionType = ( ...args: unknown[] ) => void;

function throttle(
	func: ThrottleFunctionType,
	limit: number,
): ThrottleFunctionType {
	let lastFunc: ReturnType<typeof setTimeout>;
	let lastRan = 0;
	return ( ...args: unknown[] ): void => {
		if ( !lastRan ) {
			func( ...args );
			lastRan = Date.now();
		} else {
			if ( lastFunc ) {
				clearTimeout( lastFunc );
			}
			lastFunc = setTimeout( () => {
				if ( ( Date.now() - lastRan ) >= limit ) {
					func( ...args );
					lastRan = Date.now();
				}
			}, limit - ( Date.now() - lastRan ) );
		}
	};
}

export { throttle, ThrottleFunctionType };
